/**
 * Contracts module API — Workspace (plan §5.8, §8.2 to §8.6, Appendix A
 * "Workspace" and "Versions"; §13.6 WP-55). Typed calls over the shared axios
 * instance (`services/api`), the same convention `api/client.ts`,
 * `api/rateCard.ts`, `api/notes.ts` and `api/intake.ts` use — never axios
 * directly (this repo's jest cannot parse axios's ESM build unmocked; every
 * module under src/contracts reaches it only through the mocked
 * services/api).
 *
 * A new, SEPARATE file rather than an addition to `api/client.ts`: this WP's
 * brief forbids editing client.ts (WP-52/53/54 own their backend files
 * concurrently and the MOUNT step owes the one re-export line there later —
 * `intake.ts`/`notes.ts`'s own header comments record the identical
 * decision for their own WPs).
 *
 * `applyRateCard` (Appendix A `POST /workspaces/:id/apply/rate-card`,
 * WP-52's service) and `applyPreviousStudy` (`.../apply/previous-study`,
 * WP-53's service) ARE MOUNTED: `backend/src/routes/contracts/workspace.js`
 * registers both (commit 2bf341e3 "APPLY-ROUTES"; plan gap 26 closed), and
 * `exportWorkspace`'s `POST /workspaces/:id/export` is mounted too
 * (`routes/contracts.js` wires in WP-54's `createExportsRouter`). None of
 * that makes loud failure optional: a 404 on this screen is now most often
 * a genuinely unknown or cross-site workspace/version id (§4.9, no
 * existence oracle), not an absent route, and the caller (WorkspaceTab and
 * its dialogs) still surfaces every failure loudly (G13) — see
 * `describeWorkspaceApiError` below, which names the method, the path and
 * the server's own message on a 404 rather than assuming a mount state.
 */
import api from '../../services/api';
import type { GridSheet, GridChangeEvent, ChangeSource, ChangeStatus } from '../components/grid';
// Re-exported so ChangesPanel and future consumers can import the change vocabulary
// through this api module; grid stays the owning definition.
export type { ChangeSource, ChangeStatus } from '../components/grid';

// ---------------------------------------------------------------------------
// Versions (plan §5.4, Appendix A "Versions"; mounted by WP-32). Not
// re-exported from api/client.ts today — see api/intake.ts's own
// IntakeVersion for the intake-flow shape; this is the full §5.4 row, used
// here for the opener's version picker, the R32 read-only rule and the
// export dialog's download link.
// ---------------------------------------------------------------------------

export type ContractsVersionDirection = 'received' | 'sent' | 'internal';
export type ContractsVersionSource = 'drive' | 'upload' | 'export';
export type ContractsParseStatus = 'pending' | 'running' | 'parsed' | 'failed' | 'unsupported';

/** plan §5.4 cta_versions, as GET /versions/:id and GET /negotiations/:id/versions return it. */
export interface ContractsVersion {
  id: string;
  document_id: string;
  version_no: number;
  direction: ContractsVersionDirection;
  round: number | null;
  version_date: string;
  filename: string;
  mime: string;
  storage_key: string;
  sha256: string;
  size_bytes: number | null;
  source: ContractsVersionSource;
  is_executed: boolean;
  executed_marked_by: string | null;
  parse_status: ContractsParseStatus;
  supersedes_version_id: string | null;
  drive_file_id: string | null;
  drive_modified_at: string | null;
  created_at: string;
}

/**
 * GET /negotiations/:id/versions — no frontend function existed for this
 * mounted (WP-32) route before this WP (this brief's own instruction: "add
 * `listNegotiationVersions(negotiationId)` to api/workspace.ts").
 */
export async function listNegotiationVersions(negotiationId: string): Promise<ContractsVersion[]> {
  const response = await api.get<{ versions: ContractsVersion[] }>(
    `/api/contracts/negotiations/${negotiationId}/versions`
  );
  return response.data.versions;
}

/** GET /versions/:id — used for the R32 read-only rule (direction, is_executed). */
export async function getVersion(id: string): Promise<ContractsVersion> {
  const response = await api.get<{ version: ContractsVersion }>(`/api/contracts/versions/${id}`);
  return response.data.version;
}

/** A short-TTL presigned download (plan §8.6's "the bytes are not on the export response"). */
export interface VersionDownload {
  url: string;
  expiresInSeconds: number;
  filename: string;
  mime: string;
}

/** GET /versions/:id/download. */
export async function downloadVersion(versionId: string): Promise<VersionDownload> {
  const response = await api.get<{ download: VersionDownload }>(`/api/contracts/versions/${versionId}/download`);
  return response.data.download;
}

// ---------------------------------------------------------------------------
// The workspace itself (plan §5.8, §8.2; Appendix A "Workspace"; WP-51,
// mounted).
// ---------------------------------------------------------------------------

export type WorkspaceMode = 'edit_in_place' | 'own_version';
export type WorkspaceRowStatus = 'open' | 'exported' | 'closed';

/** plan §5.8 cta_workspaces.grid — the working copy (WP-51 README). */
export interface WorkspaceGridPayload {
  format: 'cta-workspace-grid';
  version: number;
  mode: WorkspaceMode;
  base_version_id: string;
  base_parse_run_id: string | null;
  built_at: string;
  /** Never passed to BudgetGridProps.definedNames — that prop does not exist (WP-51 report). */
  named_ranges: Record<string, string>;
  coverage?: unknown;
  sheets: GridSheet[];
}

/** plan §5.8 cta_workspaces row. */
export interface WorkspaceRow {
  id: string;
  negotiation_id: string;
  base_version_id: string;
  base_parse_run_id: string | null;
  mode: WorkspaceMode;
  grid: WorkspaceGridPayload;
  lock_user_id: string | null;
  lock_at: string | null;
  lock_heartbeat_at: string | null;
  status: WorkspaceRowStatus;
}

/** §8.5's whole rule, pure data (backend workspace/lock.js lockState). */
export interface WorkspaceLockInfo {
  held: boolean;
  expired: boolean;
  user_id: string | null;
  at: string | null;
  heartbeat_at: string | null;
  expires_at: string | null;
  seconds_remaining: number | null;
  ttl_seconds: number;
}

export interface WorkspaceHolder {
  user_id: string;
  name: string | null;
}

/** The shape common to POST /lock, DELETE /lock and POST /heartbeat (workspace/README.md: "the same lock view"). */
export interface WorkspaceLockView {
  lock: WorkspaceLockInfo;
  heartbeat_seconds?: number;
  holder: WorkspaceHolder | null;
  editable: boolean;
}

export interface WorkspaceSheetSummary {
  name: string;
  hidden: boolean;
  internal: boolean;
  cells: number;
  formulas: number;
}

export interface WorkspaceSummary {
  sheets: WorkspaceSheetSummary[];
  sheet_count: number;
  cell_count: number;
  formula_count: number;
  /** The declared names' keys (workspace/grid.js gridSummary: Object.keys of named_ranges). */
  named_ranges: string[];
}

export interface WorkspaceCounts {
  proposed: number;
  accepted: number;
  rejected: number;
  total: number;
}

/** POST /workspaces { versionId, mode } -> 201 created | 200 re-opened; both are success. */
export interface WorkspaceOpenResult {
  workspace: WorkspaceRow;
  created: boolean;
  coverage?: unknown;
  lock: WorkspaceLockInfo;
  holder: WorkspaceHolder | null;
  editable: boolean;
  summary: WorkspaceSummary;
  counts: WorkspaceCounts;
}

/** GET /workspaces/:id — the changes are NOT included (workspace/README.md). */
export interface WorkspaceGetResult {
  workspace: WorkspaceRow;
  lock: WorkspaceLockInfo;
  heartbeat_seconds: number;
  holder: WorkspaceHolder | null;
  editable: boolean;
  summary: WorkspaceSummary;
  coverage?: unknown;
  counts: WorkspaceCounts;
}

export async function openWorkspace(input: { versionId: string; mode: WorkspaceMode }): Promise<WorkspaceOpenResult> {
  const response = await api.post<WorkspaceOpenResult>('/api/contracts/workspaces', input);
  return response.data;
}

export async function getWorkspace(id: string): Promise<WorkspaceGetResult> {
  const response = await api.get<WorkspaceGetResult>(`/api/contracts/workspaces/${id}`);
  return response.data;
}

// ---------------------------------------------------------------------------
// Cells and changes (plan §5.8, §8.1, R21, R24; WP-50's event shapes forward
// unchanged — the client batch_id is `grid-<base36>-<counter>` or null,
// NEVER a uuid invented here; WP-51 mints the real one).
// ---------------------------------------------------------------------------

export interface PutCellsInput {
  changes: GridChangeEvent[];
  /** Sent whenever the flushed batch contained a structural insertion (WP-50 README: no "column removed" event exists). */
  sheets?: GridSheet[];
  /** WP-57, §16.7: the browser's own idempotency key for this ONE PUT request — the same key on a retry of the same payload, a new one for a changed payload (see WorkspaceTab's flush). Absent behaves exactly as before this WP. */
  clientBatchId?: string;
}

/** plan §5.8 cta_ws_changes, as every workspace route returns it. Structurally a WsChange (components/grid), plus the DB-only columns. */
export interface WorkspaceChange {
  id: string;
  workspace_id: string;
  seq: number;
  sheet: string;
  cell_ref: string;
  old_value: string | null;
  new_value: string | null;
  old_formula: string | null;
  new_formula: string | null;
  reason: string | null;
  source: ChangeSource;
  source_ref: Record<string, unknown> | null;
  status: ChangeStatus;
  proposed_by: string | null;
  proposed_at: string;
  decided_by: string | null;
  decided_at: string | null;
  batch_id: string | null;
}

export interface PutCellsResult {
  workspace: WorkspaceRow;
  changes: WorkspaceChange[];
  summary: WorkspaceSummary;
  counts: WorkspaceCounts;
  /** WP-57: true when this response is the ORIGINAL rows of an already-stored clientBatchId, not a fresh write (§16.7 decision 5). Absent/false is an ordinary write. */
  replayed?: boolean;
}

/** PUT /workspaces/:id/cells. */
export async function putWorkspaceCells(id: string, input: PutCellsInput): Promise<PutCellsResult> {
  const response = await api.put<PutCellsResult>(`/api/contracts/workspaces/${id}/cells`, input);
  return response.data;
}

export interface ListChangesParams {
  status?: ChangeStatus;
  source?: ChangeSource;
  batchId?: string;
  sheet?: string;
}

export interface ListChangesResult {
  changes: WorkspaceChange[];
  counts: WorkspaceCounts;
}

/** GET /workspaces/:id/changes. */
export async function listWorkspaceChanges(id: string, params: ListChangesParams = {}): Promise<ListChangesResult> {
  const response = await api.get<ListChangesResult>(`/api/contracts/workspaces/${id}/changes`, { params });
  return response.data;
}

/** Appendix A's own selector: `{ ids }` a specific set, `{ all: true }` every proposed row (resolved server-side — cannot go stale between fetch and click), or `{ batchId }` one apply's rows ("Accept this apply"). */
export type ChangeSelector = { ids: string[] } | { all: true } | { batchId: string };

export interface AcceptRejectResult {
  changes: WorkspaceChange[];
  applied: number;
  summary: WorkspaceSummary;
  counts: WorkspaceCounts;
}

/** POST /workspaces/:id/changes/accept. */
export async function acceptWorkspaceChanges(id: string, selector: ChangeSelector): Promise<AcceptRejectResult> {
  const response = await api.post<AcceptRejectResult>(`/api/contracts/workspaces/${id}/changes/accept`, selector);
  return response.data;
}

/** POST /workspaces/:id/changes/reject — the working copy is never touched (R24). */
export async function rejectWorkspaceChanges(id: string, selector: ChangeSelector): Promise<AcceptRejectResult> {
  const response = await api.post<AcceptRejectResult>(`/api/contracts/workspaces/${id}/changes/reject`, selector);
  return response.data;
}

// ---------------------------------------------------------------------------
// The lock (plan §8.5, R25). POLLING, not SSE — realtimeTransport.ts has no
// contracts channel subscription today (this WP's own [OWNER] note).
// ---------------------------------------------------------------------------

/** POST /workspaces/:id/lock — or 423 with `{ error, holder }` on the body (read straight off the refusal, no second request). */
export async function lockWorkspace(id: string): Promise<WorkspaceLockView> {
  const response = await api.post<WorkspaceLockView>(`/api/contracts/workspaces/${id}/lock`);
  return response.data;
}

/** DELETE /workspaces/:id/lock[?force=true] — force needs the administrator/super_admin role (§4.2); without it, 403. */
export async function unlockWorkspace(id: string, options: { force?: boolean } = {}): Promise<WorkspaceLockView> {
  const response = await api.delete<WorkspaceLockView>(`/api/contracts/workspaces/${id}/lock`, {
    params: options.force ? { force: true } : undefined,
  });
  return response.data;
}

/** POST /workspaces/:id/heartbeat — every 30 s while `editable` is true. */
export async function heartbeatWorkspace(id: string): Promise<WorkspaceLockView> {
  const response = await api.post<WorkspaceLockView>(`/api/contracts/workspaces/${id}/heartbeat`);
  return response.data;
}

// ---------------------------------------------------------------------------
// Apply rate card (plan §8.3, WP-52). Mounted: POST /workspaces/:id/apply/
// rate-card (backend/src/routes/contracts/workspace.js, commit 2bf341e3
// "APPLY-ROUTES") — the UI still surfaces any failure loudly (G13).
// ---------------------------------------------------------------------------

export type RateCardScope = 'network' | 'site';

export interface ApplyRateCardInput {
  scope: RateCardScope;
  siteId?: string;
  therapeuticAreaId: string | null;
  /** topic -> 'card' | a position id (§8.3, §9.2, Q4). Omit on the first post. */
  picks?: Record<string, string>;
}

export interface RateCardDisagreement {
  topic: string;
  card: { value: number | null; rc_value_id: string | null };
  position: { id: string; value: number | null; basis: string; basis_ref: string };
}

export interface RateCardMatchCandidate {
  entry_id: string;
  official_name: string;
  score: number;
  matched_on: string;
}

export interface RateCardUnmatchedLine {
  line_id: string;
  label_raw: string;
  sheet: string;
  row: number;
  section: string;
  candidates: RateCardMatchCandidate[];
}

export interface RateCardSkippedLine {
  line_id: string;
  sheet: string;
  row: number;
  label_raw: string;
  section: string;
  entry_id: string | null;
  reason: string;
  detail?: unknown;
}

export interface RateCardInsertedColumn {
  sheet: string;
  role: string;
  header: string;
  col: number;
  col_letter: string;
  header_row: number;
  sponsor_cost_col: number;
  sponsor_cost_col_letter: string;
  reused: boolean;
}

export interface RateCardInsertedRow {
  sheet: string;
  row: number;
  section: string;
  run: { from: number; to: number };
  label: string;
  wcr_formula: string;
  difference_formula: string;
  overhead_applied: boolean;
}

export interface RateCardUnshiftedFormula {
  sheet: string;
  cell_ref: string;
  formula: string;
  reason: string;
}

export interface ApplyRateCardResult {
  proposals: WorkspaceChange[];
  unmatched: RateCardUnmatchedLine[];
  inserted: {
    columns: RateCardInsertedColumn[];
    rows: RateCardInsertedRow[];
    events: unknown[];
    unshifted: RateCardUnshiftedFormula[];
  };
  disagreements: RateCardDisagreement[];
  skipped: RateCardSkippedLine[];
  batch_id: string;
}

/**
 * POST /workspaces/:id/apply/rate-card (Appendix A). Mounted in
 * backend/src/routes/contracts/workspace.js as of commit 2bf341e3
 * (APPLY-ROUTES) — see this file's header comment.
 */
export async function applyRateCard(workspaceId: string, input: ApplyRateCardInput): Promise<ApplyRateCardResult> {
  const response = await api.post<ApplyRateCardResult>(
    `/api/contracts/workspaces/${workspaceId}/apply/rate-card`,
    input
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Apply previous study (plan §8.4, WP-53). Mounted: POST /workspaces/:id/
// apply/previous-study (same commit, 2bf341e3 "APPLY-ROUTES").
// ---------------------------------------------------------------------------

export type PreviousStudyWholeBudgetSource = 'previous_study' | 'rate_card';
export type PreviousStudyLineSource = 'previous_study' | 'rate_card' | 'skip';

export interface ApplyPreviousStudyParts {
  procedures?: boolean;
  fees?: boolean;
  overhead?: boolean;
  holdback?: boolean;
}

export interface ApplyPreviousStudyInput {
  sourceStudyId: string;
  sourceVersionId: string;
  parts: ApplyPreviousStudyParts;
  wholeBudgetSource: PreviousStudyWholeBudgetSource;
  lineOverrides?: Record<string, PreviousStudyLineSource>;
  scope?: RateCardScope;
  siteId?: string;
  therapeuticAreaId?: string;
  batchId?: string;
}

export interface PreviousStudyMatchedLine {
  line_id: string;
  /** apply/previousStudy.js's real row field — never `label` (WP-55 review, blocking fix 1). */
  label_raw: string;
  section: string;
  part: string;
  entry_id: string | null;
  source: PreviousStudyLineSource;
  matched_via: string;
  source_line_id: string | null;
  value: number | null;
  proposed: boolean;
  change_id?: string | null;
  reason?: string | null;
}

export interface PreviousStudyUnmatchedLine {
  line_id: string;
  label_raw: string;
  section: string;
  part: string;
  entry_id: string | null;
  candidates: RateCardMatchCandidate[];
  reason: string;
}

export interface PreviousStudyPartApplied {
  selected: boolean;
  matched?: number;
  proposed: number;
  skipped?: number;
  unmatched?: number;
  value?: number | null;
  reason?: string | null;
  sheet?: string | null;
  cell_ref?: string | null;
}

export interface ApplyPreviousStudyResult {
  proposals: WorkspaceChange[];
  matched: PreviousStudyMatchedLine[];
  unmatched: PreviousStudyUnmatchedLine[];
  partsApplied: {
    procedures: PreviousStudyPartApplied;
    fees: PreviousStudyPartApplied;
    overhead: PreviousStudyPartApplied;
    holdback: PreviousStudyPartApplied;
  };
  batchId: string;
  source: {
    study_id: string;
    protocol_number: string;
    version_id: string;
    version_no: number;
    date: string;
    negotiation_id: string;
  };
}

/**
 * POST /workspaces/:id/apply/previous-study (Appendix A). Mounted: same as
 * applyRateCard above (commit 2bf341e3, APPLY-ROUTES).
 */
export async function applyPreviousStudy(
  workspaceId: string,
  input: ApplyPreviousStudyInput
): Promise<ApplyPreviousStudyResult> {
  const response = await api.post<ApplyPreviousStudyResult>(
    `/api/contracts/workspaces/${workspaceId}/apply/previous-study`,
    input
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Export (plan §8.6, WP-54). ROUTE FILE EXISTS BUT IS NOT MOUNTED — same
// [OWNER] note; answers 404 until the MOUNT step adds
// `createExportsRouter` to routes/contracts.js.
// ---------------------------------------------------------------------------

export type ExportPathMode = 'edit_in_place' | 'own_version';
export type ExcelCheckStatus = 'pending' | 'passed' | 'failed' | 'not_required';

export interface ExportWorkspaceInput {
  pathMode: ExportPathMode;
  /** R34's "compare two versions" pair, when the export should diff two parsed versions instead of the accepted changes. */
  versionIds?: [string, string];
}

export interface ExportWorkspaceResult {
  versionId: string;
  exportId: string;
  storageKey: string;
  filename: string;
  pathMode: ExportPathMode;
  excelCheck: ExcelCheckStatus;
  summary: unknown;
  version: ContractsVersion;
  export: unknown;
  /**
   * documents/service.js:127 — `duplicateOfVersionIds.length > 0`, a
   * boolean, not a message string (WP-55 review, blocking fix 2). Never a
   * refusal: a same-hash duplicate is still filed as a new sent version
   * (§4.5); this only tells the UI whether to show the warning sentence.
   */
  duplicateWarning?: boolean;
}

/** POST /workspaces/:id/export -> 201. `pathMode` is required and never defaulted (§8.6, G3). */
export async function exportWorkspace(
  workspaceId: string,
  input: ExportWorkspaceInput
): Promise<ExportWorkspaceResult> {
  const response = await api.post<ExportWorkspaceResult>(`/api/contracts/workspaces/${workspaceId}/export`, input);
  return response.data;
}

// ---------------------------------------------------------------------------
// Loud failure (G13). Any call can fail for ordinary reasons (validation,
// lock, network, an unknown or cross-site id — §4.9, no existence oracle).
// One place decides what a person sees, naming the method and the path and
// preferring the server's own message on a 404 (WP-55 review, blocking
// fix 3) rather than asserting a mount state.
// ---------------------------------------------------------------------------

/** True shape of an axios rejection this module cares about, without importing axios's own types (never import axios directly under frontend/src/contracts). */
interface ApiErrorLike {
  response?: {
    status?: number;
    data?: { error?: string; holder?: WorkspaceHolder };
  };
  /** R-3 (WP-57): axios attaches this to every request rejection, network ones included (services/api.ts:49 reads it for the same reason) — the guard that keeps a local programming error (no response, no config) from being classified as a retryable network failure. */
  config?: unknown;
}

export function describeWorkspaceApiError(method: string, path: string, err: unknown): string {
  const apiErr = err as ApiErrorLike | undefined;
  const status = apiErr?.response?.status;
  const serverMessage = apiErr?.response?.data?.error;
  if (status === 404) {
    // Never assert a mount state (WP-55 review, blocking fix 3): both apply
    // routes and the exports router ARE mounted as of commit 2bf341e3
    // (APPLY-ROUTES), so the commonest real 404 here is an unknown or
    // cross-site workspace/version id (§4.9, no existence oracle) — prefer
    // the server's own typed message and fall back to a neutral one.
    return `${method} ${path} — 404: ${
      typeof serverMessage === 'string' && serverMessage.length > 0 ? serverMessage : 'no such route or record'
    }.`;
  }
  if (typeof serverMessage === 'string' && serverMessage.length > 0) return serverMessage;
  return `${method} ${path} failed.`;
}

/** The 423 lock-refusal body carries the holder directly (§8.5: "no second request"). */
export function lockHolderFromError(err: unknown): WorkspaceHolder | null {
  const apiErr = err as ApiErrorLike | undefined;
  if (apiErr?.response?.status !== 423) return null;
  return apiErr.response?.data?.holder ?? null;
}

/**
 * R-3 (WP-57): is a resend of the SAME payload worth trying? Yes for `429`,
 * for any status `>= 500`, and for a rejection with NO `response` at all that
 * still looks like a genuine request failure (it carries a `config` — axios
 * attaches one to every request rejection, network ones included, which is
 * exactly the case this module's replay-friendly server half was built for).
 * Never for anything else: `400`, `401`, `403`, `404`, `409`, `422`, `423`
 * and any other 4xx fall to the default here, and are told to the person at
 * once rather than retried; a bare `Error` with neither `response` nor
 * `config` (a local programming error, not a request that ever left the
 * browser) is refused the same way, so a bug is never turned into four
 * identical sends.
 *
 * This is the ONE place in the module that reads a status off an axios
 * rejection for the retry schedule — `WorkspaceTab` must never re-read
 * `err.response.status` itself.
 */
export function isRetryableWorkspaceError(err: unknown): boolean {
  const apiErr = err as ApiErrorLike | undefined;
  const status = apiErr?.response?.status;
  if (typeof status === 'number') return status === 429 || status >= 500;
  return typeof apiErr?.config !== 'undefined';
}
